import React, { useState, useEffect, useCallback } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { countries } from "../components/CountryName";
import {
    Briefcase,
    MapPin,
    Calendar,
    Trash2,
    AlertCircle,
    Building2,
    Globe,
    UserCircle2,
    Plus,
    ChevronDown,
    CheckCircle,
    Edit2
} from "lucide-react";

// Create fresh copy of empty work
const createFreshWork = (getEmptyWork) => {
    const newWork = getEmptyWork();
    return { ...newWork };
};

const EmploymentFrom = ({ workList, setWorkList, getEmptyWork, preview, handleChange, errors, setPreview, loading, onSave }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    // Ensure workList is always an array
    const safeWorkList = Array.isArray(workList) ? workList : [];

    // Initialize expanded state for work cards
    useEffect(() => {
        const initialExpandedState = {};
        safeWorkList.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeWorkList.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addWork = useCallback(() => {
        if (setWorkList && getEmptyWork) {
            setWorkList(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newWork = createFreshWork(getEmptyWork);
                const newArray = [...currentArray, newWork];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setWorkList, getEmptyWork]);

    const removeWork = useCallback((index) => {
        if (setWorkList) {
            setWorkList(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setWorkList]);

    const handleChangeField = useCallback((index, field, value) => {
        if (handleChange) {
            handleChange(index, field, value);
        }
    }, [handleChange]);

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const validateForm = () => {
        const newErrors = {};
        safeWorkList.forEach((work, i) => {
            if (!work.companyName) newErrors[`companyName_${i}`] = "Company name is required";
            if (!work.job) newErrors[`job_${i}`] = "Job field is required";
            if (!work.position) newErrors[`position_${i}`] = "Position is required";
            if (!work.city) newErrors[`city_${i}`] = "City is required";
            if (!work.country) newErrors[`country_${i}`] = "Country is required";
            if (!work.from) newErrors[`from_${i}`] = "Start date is required";
            if (!work.to) newErrors[`to_${i}`] = "End date is required";
            if (work.from && work.to && work.from > work.to) {
                newErrors[`to_${i}`] = "End date must be after start date";
            }
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview && !isEditing) {
        return (
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <Briefcase size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Employment History</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Official Career Declaration</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Employment history verified and approved</p>
                        </div>
                    )}

                    <div className="space-y-4 sm:space-y-5">
                        {safeWorkList.filter(w => w.companyName).length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <Briefcase size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No employment records added</p>
                            </div>
                        ) : (
                            safeWorkList.filter(w => w.companyName).map((w, i) => (
                                <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">{w.companyName}</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                        <Preview label="Position" value={w.position} />
                                        <Preview label="Job Field" value={w.job} />
                                        <Preview label="Company" value={w.companyName} />
                                        <Preview label="City" value={w.city} />
                                        <Preview label="Country" value={w.country} />
                                        <Preview label="Start Date" value={formatDate(w.from)} />
                                        <Preview label="End Date" value={formatDate(w.to)} />
                                        <div className="sm:col-span-2 lg:col-span-3">
                                            <Preview label="Address" value={w.companyAddress} />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Employment Information
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving employment records...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <Briefcase size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Employment History</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Add your work experience</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                            {errors?.map((err, i) => (
                                <li key={`err-${i}`} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {safeWorkList.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <Briefcase size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No employment records added</p>
                            <button
                                type="button"
                                onClick={addWork}
                                className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1 mx-auto"
                            >
                                <Plus size={16} /> Add your first employment record
                            </button>
                        </div>
                    ) : (
                        safeWorkList.map((work, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                {/* Card Header - Clickable on mobile */}
                                <div
                                    className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                                    onClick={() => toggleCard(index)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                {work.companyName || "New Employment Record"}
                                            </h3>
                                            {work.position && (
                                                <p className="text-[10px] sm:text-xs text-gray-500">
                                                    {work.position}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            className="md:hidden text-gray-400"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCard(index);
                                            }}
                                        >
                                            <ChevronDown size={18} className={`transition-transform ${expandedCards[index] ? 'rotate-180' : ''}`} />
                                        </button>
                                        {safeWorkList.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeWork(index);
                                                }}
                                                className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className={`
                                    transition-all duration-300 overflow-hidden
                                    ${expandedCards[index] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                `}>
                                    <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                            <InputField
                                                label="Company Name"
                                                icon={<Building2 size={16} />}
                                                placeholder="e.g., Google, Microsoft"
                                                value={work.companyName}
                                                onChange={(v) => handleChangeField(index, "companyName", v)}
                                                error={localErrors[`companyName_${index}`]}
                                                required
                                            />
                                            <InputField
                                                label="Job Field"
                                                icon={<Briefcase size={16} />}
                                                placeholder="e.g., Software Engineering, Marketing"
                                                value={work.job}
                                                onChange={(v) => handleChangeField(index, "job", v)}
                                                error={localErrors[`job_${index}`]}
                                                required
                                            />
                                            <InputField
                                                label="Position / Title"
                                                icon={<UserCircle2 size={16} />}
                                                placeholder="e.g., Senior Developer, Manager"
                                                value={work.position}
                                                onChange={(v) => handleChangeField(index, "position", v)}
                                                error={localErrors[`position_${index}`]}
                                                required
                                            />
                                            <InputField
                                                label="City"
                                                icon={<MapPin size={16} />}
                                                placeholder="e.g., Toronto, London"
                                                value={work.city}
                                                onChange={(v) => handleChangeField(index, "city", v)}
                                                error={localErrors[`city_${index}`]}
                                                required
                                            />

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Globe size={16} /> Country <span className="text-rose-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        value={work.country}
                                                        onChange={(e) => handleChangeField(index, "country", e.target.value)}
                                                        className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm
                                                            ${localErrors[`country_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                    >
                                                        <option value="">Select Country</option>
                                                        {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                </div>
                                                {localErrors[`country_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`country_${index}`]}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <InputField
                                                    label="Office Address"
                                                    icon={<MapPin size={16} />}
                                                    placeholder="Street address, building name, postal code"
                                                    value={work.companyAddress}
                                                    onChange={(v) => handleChangeField(index, "companyAddress", v)}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Calendar size={16} /> Start Date <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={work.from || ""}
                                                    onChange={(v) => handleChangeField(index, "from", v.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`from_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`from_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`from_${index}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Calendar size={16} /> End Date <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={work.to || ""}
                                                    onChange={(v) => handleChangeField(index, "to", v.target.value)}
                                                    min={work.from}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`to_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`to_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`to_${index}`]}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addWork}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 py-3 rounded-xl font-semibold text-sm transition-all"
                        >
                            <Plus size={18} /> Add Another Employment Record
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
};

/* UI HELPERS */
const InputField = ({ label, value, onChange, type = "text", placeholder, icon, min, max, error, required }) => (
    <div>
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
            {icon} {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
            type={type}
            value={value || ""}
            min={min}
            max={max}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

const Preview = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</span>
        <span className="text-gray-800 font-semibold text-sm break-words">{value || "—"}</span>
    </div>
);

export default EmploymentFrom;